import type { Bucket, StorageFile } from "@shared/types/storage";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "./client";

export function useBuckets() {
  return useQuery({
    queryKey: ["storage", "buckets"],
    queryFn: async () => {
      const { data, error } = await supabase.storage.listBuckets();
      if (error) throw error;
      return data as Bucket[];
    },
  });
}

export function useCreateBucket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      name,
      isPublic,
    }: {
      name: string;
      isPublic: boolean;
    }) => {
      const { error } = await supabase.storage.createBucket(name, {
        public: isPublic,
      });
      if (error) throw error;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["storage", "buckets"] }),
  });
}

export function useDeleteBucket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const { error } = await supabase.storage.deleteBucket(name);
      if (error) throw error;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["storage", "buckets"] }),
  });
}

export function useFiles(bucket: string, path = "") {
  return useQuery({
    queryKey: ["storage", "files", bucket, path],
    queryFn: async () => {
      const { data, error } = await supabase.storage.from(bucket).list(path, {
        limit: 100,
        sortBy: { column: "updated_at", order: "desc" },
      });
      if (error) throw error;
      return data as StorageFile[];
    },
    enabled: !!bucket,
  });
}

export function useUploadFile(bucket: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, path }: { file: File; path?: string }) => {
      const filePath = path ? `${path}/${file.name}` : file.name;
      const { error } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, { upsert: true });
      if (error) throw error;
      return filePath;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["storage", "files", bucket],
      }),
  });
}

export function useDeleteFile(bucket: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (paths: string[]) => {
      const { error } = await supabase.storage.from(bucket).remove(paths);
      if (error) throw error;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["storage", "files", bucket],
      }),
  });
}

export function useGetFileUrl(bucket: string) {
  return (path: string) => {
    const { data } = supabase.storage.from(bucket).getPublicUrl(path);
    return data.publicUrl;
  };
}

export function useDownloadFile(bucket: string) {
  return useMutation({
    mutationFn: async (path: string) => {
      const { data, error } = await supabase.storage
        .from(bucket)
        .download(path);
      if (error) throw error;
      return data;
    },
  });
}
